import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { snackbar } from '@occc-app/design-system';
import { RetailerNetworkLocationFilter } from '@occc/graphql';
import { useBulkSelect } from '@occc-app/hooks/useBulkSelect';
import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';

import LocationList from './location-list.component';
import { RemoveLocationFromRetailerNetworkDialog } from './remove-location-from-retailer-network-dialog.component';

import { useRemoveLocationFromRetailerNetworkMutation } from '../../../data-access/mutations';
import { useGetRetailerNetworkLocations } from '../../../data-access/queries';
import {
  LOCATIONS_AFTER_PARAM,
  LOCATIONS_BEFORE_PARAM,
} from '../retailer-network-locations-configuration.constants';
import { routeCreator } from '../../retailer-connect.utils';

type LocationListContainerProps = ReturnType<typeof useBulkSelect> & {
  filter: RetailerNetworkLocationFilter;
  handleOpenAddLocationsToLocationGroupDialog: () => void;
  retailerNetworkUuid: string;
};

export const LocationListContainer = ({
  filter,
  bulkSelectedItems,
  handleBulkSelectMultiple,
  handleBulkDeselectAll,
  handleItemSelect,
  handleOpenAddLocationsToLocationGroupDialog,
  retailerNetworkUuid,
}: LocationListContainerProps) => {
  const navigate = useNavigate();
  const [removeLocationDialogOpen, setRemoveLocationDialogOpen] = useState<boolean>(false);
  const [locationToRemoveUuid, setLocationToRemoveUuid] = useState<string | null>(null);

  const [paginationInfo, onPageChange] = usePaginationParams({
    beforeParamName: LOCATIONS_BEFORE_PARAM,
    afterParamName: LOCATIONS_AFTER_PARAM,
  });

  const locationsData = useGetRetailerNetworkLocations({
    filter,
    paginationInfo,
    onPageChange,
  });

  const onCompleted = () => {
    if (locationToRemoveUuid) {
      if (bulkSelectedItems.has(locationToRemoveUuid)) {
        handleItemSelect(locationToRemoveUuid);
      }

      setLocationToRemoveUuid(null);
    }

    setRemoveLocationDialogOpen(false);
    snackbar.success('Location was removed from the Retailer Network.');
  };
  const onError = () => {
    snackbar.error('Unable to remove Location from the Retailer Network. Please try again later.');
  };

  const [
    removeLocationFromRetailerNetwork,
    { loading: isRemoveLocationFromRetailerNetworkInProgress },
  ] = useRemoveLocationFromRetailerNetworkMutation({
    onCompleted,
    onError,
    refetchQueries: ['retailerNetwork', 'retailerNetworkLocations'],
  });

  const onConfirm = () => {
    if (locationToRemoveUuid) {
      removeLocationFromRetailerNetwork({
        variables: {
          retailerNetworkUuid,
          locationUuid: locationToRemoveUuid,
        },
      });
    }
  };

  const onRemoveLocationMenuItemClick = (locationUuid: string) => {
    setRemoveLocationDialogOpen(true);
    setLocationToRemoveUuid(locationUuid);
  };

  const handleOpenLocationDetailsPage = useCallback(
    (locationUuid: string) => {
      navigate(routeCreator.locationDetails(retailerNetworkUuid, locationUuid));
    },
    [navigate, retailerNetworkUuid],
  );

  return (
    <>
      <LocationList
        locationsData={locationsData}
        bulkSelectedItems={bulkSelectedItems}
        handleBulkSelectMultiple={handleBulkSelectMultiple}
        handleBulkDeselectAll={handleBulkDeselectAll}
        handleItemSelect={handleItemSelect}
        handleOpenAddLocationsToLocationGroupDialog={handleOpenAddLocationsToLocationGroupDialog}
        onRemoveLocationMenuItemClick={onRemoveLocationMenuItemClick}
        handleOpenLocationDetailsPage={handleOpenLocationDetailsPage}
      />
      <RemoveLocationFromRetailerNetworkDialog
        open={removeLocationDialogOpen}
        setOpen={setRemoveLocationDialogOpen}
        onConfirm={onConfirm}
        isRemoveLocationFromRetailerNetworkInProgress={
          isRemoveLocationFromRetailerNetworkInProgress
        }
      />
    </>
  );
};
